import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonModal,
  IonList,
  IonItem,
  IonLabel,
  IonSpinner,
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
} from '@ionic/react';
import {
  downloadOutline,
  cloudUploadOutline,
  documentTextOutline,
  pricetagOutline,
  globeOutline,
  sparklesOutline,
  closeOutline,
  checkmarkCircle,
  ticketOutline,
  cartOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api, {
  userService,
  eventService,
  saleService,
  ticketService,
} from '../services/api';
import { Event } from '../types';
import { format } from 'date-fns';
import {
  generateSalesTemplate,
  generateInventoryTemplate,
  parseSalesExcel,
  parseInventoryExcel,
} from '../utils/excelUtils';
import { eventBus, EVENTS } from '../utils/eventBus';

const Import: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState<'sales' | 'inventory'>('sales');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [showSiteImportModal, setShowSiteImportModal] = useState(false);
  const [siteImportSource, setSiteImportSource] = useState<
    'livefootballtickets' | 'stubhub' | 'footballticketnet' | 'tixstock' | 'vividseats' | null
  >(null);
  const [siteImportFile, setSiteImportFile] = useState<File | null>(null);
  const [siteImportLoading, setSiteImportLoading] = useState(false);
  const [siteImportAnalysis, setSiteImportAnalysis] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [activeSegment, setActiveSegment] = useState<'custom' | 'sites'>(
    'sites'
  );

  useEffect(() => {
    loadEvents();

    // Subscribe to event bus for refreshing events
    const unsubscribeEvents = eventBus.on(EVENTS.REFRESH_EVENTS, () => {
      console.log('Import: Refreshing events due to import');
      loadEvents();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribeEvents();
    };
  }, []);

  const loadEvents = async () => {
    try {
      const eventsData = await eventService.getAllEvents();
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
      setToastMessage('Failed to load events');
      setShowToast(true);
    }
  };

  const handleDownloadTemplate = (type: 'sales' | 'inventory') => {
    try {
      if (type === 'sales') {
        generateSalesTemplate();
        setToastMessage('Sales template downloaded successfully');
      } else {
        generateInventoryTemplate();
        setToastMessage('Inventory template downloaded successfully');
      }
      setShowToast(true);
    } catch (error) {
      console.error('Error downloading template:', error);
      setToastMessage('Failed to download template');
      setShowToast(true);
    }
  };

  const handleImportCustom = async () => {
    if (!importFile) {
      setToastMessage('Please select a file to import');
      setShowToast(true);
      return;
    }

    setImporting(true);
    try {
      if (importType === 'sales') {
        const parsedSales = await parseSalesExcel(importFile, events);

        let successCount = 0;
        let errorCount = 0;

        for (const saleData of parsedSales) {
          try {
            await saleService.create(saleData as any);
            successCount++;
          } catch (error) {
            console.error('Error importing sale:', error);
            errorCount++;
          }
        }

        setToastMessage(
          `Imported ${successCount} sales successfully${
            errorCount > 0 ? `, ${errorCount} failed` : ''
          }`
        );
        eventBus.emit(EVENTS.REFRESH_SALES);
      } else {
        try {
          const parsedInventory = await parseInventoryExcel(importFile, events);

          let successCount = 0;
          let errorCount = 0;
          const detailedErrors: string[] = [];

          for (let i = 0; i < parsedInventory.length; i++) {
            const ticketData = parsedInventory[i];
            try {
              await ticketService.create(ticketData as any);
              successCount++;
            } catch (error) {
              let errorMsg = 'Unknown error';
              if (error instanceof Error) {
                errorMsg = error.message;
              }
              // Extract validation errors from API response
              if (error && typeof error === 'object' && 'response' in error) {
                const apiError = error as any;
                if (apiError.response?.data?.message) {
                  if (Array.isArray(apiError.response.data.message)) {
                    errorMsg = apiError.response.data.message.join(', ');
                  } else {
                    errorMsg = apiError.response.data.message;
                  }
                }
              }
              console.error(`Error importing ticket row ${i + 2}:`, error);
              console.error(`Ticket data for row ${i + 2}:`, ticketData);
              detailedErrors.push(`Row ${i + 2}: ${errorMsg}`);
              errorCount++;
            }
          }

          if (detailedErrors.length > 0) {
            console.error('Detailed import errors:', detailedErrors);
          }

          setToastMessage(
            `Imported ${successCount} tickets successfully${
              errorCount > 0 ? `, ${errorCount} failed` : ''
            }`
          );
          eventBus.emit(EVENTS.REFRESH_TICKETS);
        } catch (parseError) {
          console.error('Excel parsing error:', parseError);
          const errorMessage = parseError instanceof Error ? parseError.message : 'Failed to parse Excel file';
          setToastMessage(`Import failed: ${errorMessage}`);
          throw parseError;
        }
      }

      setShowImportModal(false);
      setImportFile(null);
      setShowToast(true);
    } catch (error: any) {
      console.error('Error importing custom file:', error);
      setToastMessage(error.message || 'Failed to import custom file');
      setShowToast(true);
    } finally {
      setImporting(false);
    }
  };

  const handleSiteImportClick = (
    source: 'livefootballtickets' | 'stubhub' | 'footballticketnet' | 'tixstock' | 'vividseats'
  ) => {
    setSiteImportSource(source);
    setSiteImportFile(null);
    setSiteImportAnalysis(null);
    setShowSiteImportModal(true);
  };

  const handleSiteFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type based on source
    const isValidFile = () => {
      if (siteImportSource === 'stubhub' || siteImportSource === 'tixstock' || siteImportSource === 'vividseats') {
        // StubHub, Tixstock, and Vivid Seats accept both CSV and Excel
        return file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      } else {
        // Other sources (LiveFootballTickets, FootballTicketNet) only accept Excel
        return file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      }
    };

    if (!isValidFile()) {
      const allowedFormats = (siteImportSource === 'stubhub' || siteImportSource === 'tixstock' || siteImportSource === 'vividseats')
        ? '.csv, .xlsx, or .xls' 
        : '.xlsx or .xls';
      setToastMessage(`Please select a valid file (${allowedFormats})`);
      setShowToast(true);
      return;
    }

    setSiteImportFile(file);
    setSiteImportLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let response;
      if (siteImportSource === 'tixstock') {
        // For Tixstock, we use the file upload endpoint first
        response = await api.post('/import/tixstock', formData);
        
        // Transform event details to match the expected analysis format
        const eventPreview = (response.data.eventDetails || []).map((event: any) => ({
          eventName: event.name,
          eventDate: new Date(event.date),
          venue: event.venue,
          salesCount: event.salesCount,
          isNew: event.isNew,
          existingEventId: event.existingEventId
        }));
        
        // Transform the response to match the expected analysis format
        setSiteImportAnalysis({
          totalRows: response.data.totalRows,
          totalEvents: response.data.successfulEvents,
          newEvents: response.data.newEvents || 0,
          existingEvents: response.data.existingEvents || 0,
          eventPreview: eventPreview,
          sheetName: 'Tixstock CSV',
          directImport: true, // Flag to indicate this is already imported
          importResult: response.data
        });
      } else {
        response = await api.post(
          `/sales/import/analyze?source=${siteImportSource}`,
          formData
        );
        setSiteImportAnalysis(response.data);
      }
    } catch (error: any) {
      console.error('Import analysis error:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to analyze file';
      setToastMessage(errorMessage);
      setShowToast(true);
      setSiteImportFile(null);
    } finally {
      setSiteImportLoading(false);
    }
  };

  const handleSiteImportConfirm = async () => {
    if (!siteImportFile || !siteImportSource) return;

    // For Tixstock, the import is already done during analysis
    if (siteImportSource === 'tixstock' && siteImportAnalysis?.directImport) {
      const result = siteImportAnalysis.importResult;
      let message = `Tixstock import completed! `;
      message += `Events: ${result.successfulEvents} (${result.newEvents || 0} new, ${result.existingEvents || 0} existing), `;
      message += `Sales: ${result.successfulSales} new`;
      if (result.skippedSales > 0) {
        message += `, ${result.skippedSales} skipped (already exist)`;
      }
      if (result.errors?.length > 0) {
        message += `, ${result.errors.length} errors`;
      }
      
      setToastMessage(message);
      setShowToast(true);
      setShowSiteImportModal(false);

      // Emit events for sales and events refresh
      eventBus.emit(EVENTS.REFRESH_SALES);
      eventBus.emit(EVENTS.REFRESH_EVENTS);
      return;
    }

    setSiteImportLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', siteImportFile);

      const response = await api.post(
        `/sales/import/execute?source=${siteImportSource}`,
        formData
      );

      const result = response.data;

      let message = `Import completed! Imported: ${result.imported} sales`;
      if (result.skipped > 0) message += `, Skipped: ${result.skipped}`;
      if (result.failed > 0) message += `, Failed: ${result.failed}`;
      if (result.createdEvents > 0)
        message += `, Created ${result.createdEvents} new events`;

      setToastMessage(message);
      setShowToast(true);
      setShowSiteImportModal(false);

      // Emit events for sales and events refresh
      eventBus.emit(EVENTS.REFRESH_SALES);
      eventBus.emit(EVENTS.REFRESH_EVENTS);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to import sales';
      setToastMessage(errorMessage);
      setShowToast(true);
    } finally {
      setSiteImportLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>Import Data</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Import Data</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="p-4">
          <IonSegment
            value={activeSegment}
            onIonChange={(e) =>
              setActiveSegment(e.detail.value as 'custom' | 'sites')
            }
          >
            <IonSegmentButton value="sites">
              <IonLabel>
                <IonIcon icon={globeOutline} className="mb-1" />
                <div className="text-xs mt-1">Site Import</div>
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="custom">
              <IonLabel>
                <IonIcon icon={documentTextOutline} className="mb-1" />
                <div className="text-xs mt-1">Custom Import</div>
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {activeSegment === 'custom' ? (
            <IonGrid className="p-0 mt-6">
              <IonRow className="g-4">
                {/* Sales Import Card */}
                <IonCol size="12" sizeMd="6">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="flex items-center">
                        <IonIcon icon={pricetagOutline} className="mr-2" />
                        Custom Sales Import
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import multiple sales at once using an Excel file.
                          Download the template to see the required format.
                        </p>
                      </IonText>

                      <div className="flex flex-col gap-2">
                        <IonButton
                          expand="block"
                          fill="outline"
                          onClick={() => handleDownloadTemplate('sales')}
                        >
                          <IonIcon icon={downloadOutline} slot="start" />
                          Download Sales Template
                        </IonButton>

                        <IonButton
                          expand="block"
                          onClick={() => {
                            setImportType('sales');
                            setShowImportModal(true);
                          }}
                        >
                          <IonIcon icon={cloudUploadOutline} slot="start" />
                          Import Custom Sales
                        </IonButton>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* Inventory Import Card */}
                <IonCol size="12" sizeMd="6">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="flex items-center">
                        <IonIcon icon={documentTextOutline} className="mr-2" />
                        Custom Inventory Import
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import multiple inventory items at once using an Excel
                          file. Download the template to see the required
                          format.
                        </p>
                      </IonText>

                      <div className="flex flex-col gap-2">
                        <IonButton
                          expand="block"
                          fill="outline"
                          onClick={() => handleDownloadTemplate('inventory')}
                        >
                          <IonIcon icon={downloadOutline} slot="start" />
                          Download Inventory Template
                        </IonButton>

                        <IonButton
                          expand="block"
                          onClick={() => {
                            setImportType('inventory');
                            setShowImportModal(true);
                          }}
                        >
                          <IonIcon icon={cloudUploadOutline} slot="start" />
                          Import Custom Inventory
                        </IonButton>
                      </div>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
            </IonGrid>
          ) : activeSegment === 'sites' ? (
            <IonGrid className="p-0 mt-6">
              <IonRow className="g-4">
                {/* LiveFootballTickets Import Card */}
                <IonCol size="12" sizeMd="3">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="text-base">
                        LiveFootballTickets
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import sales from LiveFootballTickets exports.
                        </p>
                      </IonText>

                      <IonButton
                        expand="block"
                        onClick={() =>
                          handleSiteImportClick('livefootballtickets')
                        }
                      >
                        <IonIcon icon={cloudUploadOutline} slot="start" />
                        Import File
                      </IonButton>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* StubHub Import Card */}
                <IonCol size="12" sizeMd="3">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="text-base">StubHub</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import sales from StubHub seller reports. Supports both CSV and Excel formats.
                        </p>
                      </IonText>

                      <IonButton
                        expand="block"
                        onClick={() => handleSiteImportClick('stubhub')}
                      >
                        <IonIcon icon={cloudUploadOutline} slot="start" />
                        Import File
                      </IonButton>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* FootballTicketNet Import Card */}
                <IonCol size="12" sizeMd="3">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="text-base">
                        FootballTicketNet
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import sales from FootballTicketNet reports.
                        </p>
                      </IonText>

                      <IonButton
                        expand="block"
                        onClick={() =>
                          handleSiteImportClick('footballticketnet')
                        }
                      >
                        <IonIcon icon={cloudUploadOutline} slot="start" />
                        Import File
                      </IonButton>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* Tixstock Import Card */}
                <IonCol size="12" sizeMd="3">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="text-base">
                        Tixstock
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import sales from Tixstock CSV exports. Supports CSV/TSV format.
                        </p>
                      </IonText>

                      <IonButton
                        expand="block"
                        onClick={() =>
                          handleSiteImportClick('tixstock')
                        }
                      >
                        <IonIcon icon={cloudUploadOutline} slot="start" />
                        Import File
                      </IonButton>
                    </IonCardContent>
                  </IonCard>
                </IonCol>

                {/* Vivid Seats Import Card */}
                <IonCol size="12" sizeMd="3">
                  <IonCard className="h-full">
                    <IonCardHeader>
                      <IonCardTitle className="text-base">
                        Vivid Seats
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent className="space-y-4">
                      <IonText>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Import sales from Vivid Seats CSV exports. Supports CSV and Excel formats.
                        </p>
                      </IonText>

                      <IonButton
                        expand="block"
                        onClick={() =>
                          handleSiteImportClick('vividseats')
                        }
                      >
                        <IonIcon icon={cloudUploadOutline} slot="start" />
                        Import File
                      </IonButton>
                    </IonCardContent>
                  </IonCard>
                </IonCol>
              </IonRow>
            </IonGrid>
          ) : null}
        </div>

        {/* Import Custom Modal */}
        <IonModal
          isOpen={showImportModal}
          onDidDismiss={() => {
            setShowImportModal(false);
            setImportFile(null);
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                Import {importType === 'sales' ? 'Sales' : 'Inventory'} from
                Custom File
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowImportModal(false)}>
                  Cancel
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Upload Custom File</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-4">
                  <IonText>
                    <p className="text-sm text-gray-600">
                      Please upload a custom file using the template format.
                      Download the template first to see the required columns.
                    </p>
                  </IonText>

                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setImportFile(file);
                      }
                    }}
                    className="w-full p-2 border rounded"
                  />

                  {importFile && (
                    <IonText color="medium">
                      <p className="text-sm">Selected: {importFile.name}</p>
                    </IonText>
                  )}

                  <div className="flex gap-2 mt-4">
                    <IonButton
                      expand="block"
                      onClick={handleImportCustom}
                      disabled={!importFile || importing}
                      className="flex-1"
                    >
                      {importing ? (
                        <>
                          <IonSpinner name="crescent" className="mr-2" />
                          Importing...
                        </>
                      ) : (
                        `Import ${
                          importType === 'sales' ? 'Sales' : 'Inventory'
                        }`
                      )}
                    </IonButton>

                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={() => handleDownloadTemplate(importType)}
                      className="flex-1"
                    >
                      <IonIcon icon={downloadOutline} slot="start" />
                      Download Template
                    </IonButton>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          </IonContent>
        </IonModal>

        {/* Site Import Modal */}
        <IonModal
          isOpen={showSiteImportModal}
          onDidDismiss={() => {
            setShowSiteImportModal(false);
            setSiteImportFile(null);
            setSiteImportAnalysis(null);
            setSiteImportLoading(false);
          }}
          className="import-modal"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle className="text-lg font-semibold">
                <IonIcon icon={cartOutline} className="mr-2 align-middle" />
                Import Sales from{' '}
                {siteImportSource
                  ?.replace('livefootballtickets', 'LiveFootballTickets')
                  .replace('stubhub', 'StubHub')
                  .replace('footballticketnet', 'FootballTicketNet')
                  .replace('tixstock', 'Tixstock')
                  .replace('vividseats', 'Vivid Seats')}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => {
                    setShowSiteImportModal(false);
                    setSiteImportFile(null);
                    setSiteImportAnalysis(null);
                    setSiteImportLoading(false);
                  }}
                >
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="max-w-6xl mx-auto">
              {!siteImportFile ? (
                <div>
                  <h3 className="text-lg font-medium mb-4">Select File</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Upload a file {(siteImportSource === 'stubhub' || siteImportSource === 'tixstock') ? '(.csv, .xlsx, or .xls)' : '(.xlsx or .xls)'} containing sales data from{' '}
                    {siteImportSource}. The system will automatically match
                    sales to existing events based on event details.
                  </p>

                  {/* Required columns info */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                      Required columns for {siteImportSource}:
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside">
                      {siteImportSource === 'livefootballtickets' && (
                        <>
                          <li>SALEID</li>
                          <li>SALE DATE</li>
                          <li>EVENT</li>
                          <li>EVENT DATE</li>
                          <li>VENUE NAME</li>
                          <li>QUANTITY</li>
                          <li>TOTAL</li>
                        </>
                      )}
                      {siteImportSource === 'stubhub' && (
                        <>
                          <li>Order Id</li>
                          <li>Event Name</li>
                          <li>Event Date</li>
                          <li>Venue Name</li>
                          <li>Section</li>
                          <li>Quantity</li>
                          <li>Sell Price</li>
                          <li>Sale Date</li>
                        </>
                      )}
                      {siteImportSource === 'footballticketnet' && (
                        <>
                          <li>Match Date</li>
                          <li>Match Name</li>
                          <li>Category Name</li>
                          <li>Total Quantity Sold</li>
                          <li>Price Sold Per Ticket</li>
                        </>
                      )}
                      {siteImportSource === 'tixstock' && (
                        <>
                          <li>Order ID</li>
                          <li>Event Description</li>
                          <li>Event Date</li>
                          <li>Venue</li>
                          <li>Ticket Qty</li>
                          <li>Ticket Price</li>
                          <li>Transaction Value</li>
                          <li>Customer First Name / Customer Last Name</li>
                          <li>Customer Email</li>
                        </>
                      )}
                    </ul>
                  </div>

                  <div className="relative">
                    <input
                      type="file"
                      accept={(siteImportSource === 'stubhub' || siteImportSource === 'tixstock' || siteImportSource === 'vividseats') ? '.csv,.xlsx,.xls' : '.xlsx,.xls'}
                      onChange={handleSiteFileSelect}
                      className="w-full p-8 border-3 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                      id="site-file-upload"
                    />
                    <label
                      htmlFor="site-file-upload"
                      className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                    >
                      <IonIcon
                        icon={sparklesOutline}
                        className="text-4xl text-gray-400 mb-2"
                      />
                      <span className="text-gray-600 dark:text-gray-400">
                        Click to select or drag and drop {(siteImportSource === 'stubhub' || siteImportSource === 'tixstock') ? '(CSV or Excel file)' : '(Excel file)'}
                      </span>
                    </label>
                  </div>
                </div>
              ) : siteImportLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <IonSpinner
                    name="crescent"
                    className="w-12 h-12 mb-4"
                    color="primary"
                  />
                  <p className="text-gray-600 dark:text-gray-400">
                    Analyzing file...
                  </p>
                </div>
              ) : siteImportAnalysis ? (
                <div>
                  <h3 className="text-lg font-medium mb-4">Import Preview</h3>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 animate-fadeIn">
                    <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {siteImportAnalysis.totalRows}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Total Sales
                      </p>
                    </div>
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {siteImportAnalysis.totalEvents}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Total Events
                      </p>
                    </div>
                    <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {siteImportAnalysis.newEvents}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        New Events
                      </p>
                    </div>
                    <div className="bg-orange-100 dark:bg-orange-900/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                        {siteImportAnalysis.existingEvents}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Existing Events
                      </p>
                    </div>
                  </div>

                  {/* Events Preview */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-lg">Events to Import</h4>
                      <span className="text-sm text-gray-500">
                        {siteImportAnalysis.eventPreview?.length || 0} events
                        found
                      </span>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow-inner p-1 max-h-[400px] overflow-hidden">
                      <div className="overflow-y-auto overflow-x-auto max-h-[392px] custom-scrollbar">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-white dark:bg-gray-900 z-10">
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="p-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Event
                              </th>
                              <th className="p-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Date & Time
                              </th>
                              <th className="p-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Venue
                              </th>
                              <th className="p-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Sales
                              </th>
                              <th className="p-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-900">
                            {siteImportAnalysis.eventPreview?.map(
                              (event: any, idx: number) => (
                                <tr
                                  key={idx}
                                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                  <td className="p-3">
                                    <div className="font-medium text-gray-900 dark:text-gray-100">
                                      {event.eventName}
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <div className="text-gray-900 dark:text-gray-100">
                                      {format(
                                        new Date(event.eventDate),
                                        'MMM dd, yyyy'
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {format(
                                        new Date(event.eventDate),
                                        'HH:mm'
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <div className="text-gray-900 dark:text-gray-100">
                                      {event.venue}
                                    </div>
                                    {event.city && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {event.city}
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-3 text-center">
                                    <IonChip color="primary" className="m-0">
                                      <IonLabel className="px-1">
                                        {event.salesCount}
                                      </IonLabel>
                                    </IonChip>
                                  </td>
                                  <td className="p-3 text-center">
                                    {event.isNew ? (
                                      <IonChip color="success" className="m-0">
                                        <IonIcon
                                          icon={sparklesOutline}
                                          className="mr-1"
                                        />
                                        <IonLabel>New</IonLabel>
                                      </IonChip>
                                    ) : (
                                      <IonChip color="medium" className="m-0">
                                        <IonIcon
                                          icon={checkmarkCircle}
                                          className="mr-1"
                                        />
                                        <IonLabel>Existing</IonLabel>
                                      </IonChip>
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* File Info */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <IonIcon
                        icon={sparklesOutline}
                        className="text-blue-500 text-xl mr-3 mt-0.5"
                      />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                          File Information
                        </h4>
                        <div className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                          <p>
                            <span className="font-medium">File:</span>{' '}
                            {siteImportFile?.name}
                          </p>
                          <p>
                            <span className="font-medium">Size:</span>{' '}
                            {(siteImportFile?.size / 1024).toFixed(2)} KB
                          </p>
                          <p>
                            <span className="font-medium">Sheet:</span>{' '}
                            {siteImportAnalysis.sheetName}
                          </p>
                          <p>
                            <span className="font-medium">Total Rows:</span>{' '}
                            {siteImportAnalysis.totalRows}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 sticky bottom-0 bg-white dark:bg-gray-900 pt-3 pb-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleSiteImportConfirm}
                      disabled={siteImportLoading}
                      className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                    >
                      <IonIcon icon={checkmarkCircle} className="text-lg" />
                      <span>
                        {siteImportAnalysis.directImport 
                          ? 'Complete Import'
                          : `Import ${siteImportAnalysis.totalRows} Sales`
                        }
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setSiteImportFile(null);
                        setSiteImportAnalysis(null);
                      }}
                      className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02] flex items-center justify-center gap-2"
                    >
                      <IonIcon icon={closeOutline} className="text-lg" />
                      <span>Choose Different</span>
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </IonContent>
        </IonModal>

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  );
};

export default Import;
